import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import {
  shouldFallbackToStoreApi,
  useCloudConnectSubscription,
} from 'src/api/queries';
import { CloudConnectPaymentPage } from 'src/apps/manageSubscription/app/pages/cloudConnect/CloudConnectPaymentPage';
import { PaymentPage } from 'src/apps/manageSubscription/app/pages/PaymentPage';
import { Spinner } from 'src/components';
import { z } from 'zod';

import { Route as SubscriptionsRoute } from '../subscriptions';

const defaultSearch = {
  openBillingInfo: undefined,
  updateBillingInterval: undefined,
  subscription_id: undefined,
  setup_intent: undefined,
  setup_intent_client_secret: undefined,
  redirect_status: undefined,
};

const searchSchema = z.object({
  openBillingInfo: z.boolean().optional(),
  subscription_id: z.string().optional(),
  setup_intent: z.string().optional(),
  setup_intent_client_secret: z.string().optional(),
  redirect_status: z
    .enum(['success', 'succeeded', 'failure', 'failed'])
    .optional(),
  updateBillingInterval: z
    .preprocess(
      (val) => {
        // 'true' will just open the modal on whatever variant is the opposite of the current one.
        // 'monthly' and 'annually' can be used to directly open the modal with the respective interval selected,
        // but they will get a message in the modal that they are already on that plan.
        if (['monthly', 'annual', 'biennial', 'true'].includes(val as string)) {
          return val;
        }

        // Accept boolean true
        if (val === true) {
          return 'true';
        }

        // Allow simply setting the query param without a value to open the modal, e.g. ?updateBillingInterval
        if (val === '') {
          return 'true';
        }

        return undefined;
      },
      z.enum(['monthly', 'annual', 'biennial', 'true'])
    )
    .optional(),
});

export const Route = createFileRoute('/_auth/_layout/subscriptions/payment')({
  component: () => <SubscriptionPaymentRoute />,
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams(defaultSearch)] },
});

const SubscriptionPaymentRoute = () => {
  const { useStoreApi } = SubscriptionsRoute.useLoaderData();
  const cloudConnectSubscription = useCloudConnectSubscription({
    enabled: !useStoreApi,
  });

  if (useStoreApi) {
    return <PaymentPage />;
  }

  if (cloudConnectSubscription.isPending) {
    return <Spinner />;
  }

  const subscription = cloudConnectSubscription.data ?? null;

  if (shouldFallbackToStoreApi(subscription, cloudConnectSubscription.error)) {
    return <PaymentPage />;
  }

  if (cloudConnectSubscription.error) throw cloudConnectSubscription.error;

  return <CloudConnectPaymentPage subscription={subscription} />;
};
